import { getLogger } from '@trimble/logging';
import { Web3DViewer } from '@technology/web3d';
import { Subscription } from 'rxjs';
import { detectArrayChanges, ThreeJsManager } from '@trimble/utilities';
import {
  FULLY_OPAQUE_STATION_OPACITY_FRACTION,
  ImageModel,
  ImageStation,
  PositioningData,
  StationsCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  Setter,
} from 'solid-js';
import { DEFAULT_FIELD_OF_VIEW } from '../helpers/camera/camera-constants';
import { Web3dSettings } from '../models/web3d-settings';
import { StationNavigationController } from './models/station-navigation-controller';
import { StationsNavigationCapability } from './stations-navigation-capability';

/**
 * Opacity for the "visible" (current) station. Re-exports the shared
 * `FULLY_OPAQUE_STATION_OPACITY_FRACTION` (0.999): staying strictly below
 * 1.0 keeps the three.js material's `transparent` flag stable, avoiding a
 * WebGL shader recompile (and visible flash) during station transitions.
 * The 0.1 % opacity difference is visually imperceptible, and explicit
 * `renderOrder` / `depthTest` on planar and panorama models keep draw order
 * correct regardless of the flag.
 */
const OPACITY_VISIBLE = FULLY_OPAQUE_STATION_OPACITY_FRACTION;

/** Opacity value used for hidden (preloaded / non-current) stations. */
const OPACITY_HIDDEN = 0;

export interface ImageCapabilityOpacityOptions {
  resolveVisibleStationOpacity?(): number;
}

const TEXTURE_LOAD_TIMEOUT_MS = 30_000;

export abstract class ImageCapabilityBase
  extends StationsNavigationCapability
  implements StationNavigationController
{
  public isImageVisible: Accessor<boolean>;
  public setIsImageVisible: Setter<boolean>;

  /** Whether the current station's image tileset is loaded and ready to display. */
  public readonly isCurrentStationImageReady: Accessor<boolean>;
  private readonly setIsCurrentStationImageReady: Setter<boolean>;

  public readonly isImageLoading: Accessor<boolean>;
  protected readonly setIsImageLoading: Setter<boolean>;

  /**
   * Loaded-index → live `stationList[index]` ref at load time. The stored
   * ref (NOT the resolved/patched `tilesetCache` ref) is what the eviction
   * loop compares against `stationList[index]` to detect in-place mutations
   * (e.g. panorama rotation). The `.station.id` is also used to unload by
   * id after the upstream list has been cleared.
   */
  private readonly loadedStations: Map<number, ImageStation> = new Map();

  /** Incremented each time the loading effect runs; used to ignore stale async completions. */
  private loadingEffectRunId = 0;

  /** Tracks the previous effect's station index to detect cache-only re-runs. */
  private lastEffectStationIndex: number | null = null;

  /** Tracks the previous effect's station ID so a different station at the same index is detected. */
  private lastEffectStationId: string | null = null;

  /** Tracks the previous effect's image visibility to detect visibility toggles. */
  private lastEffectImageVisible = true;

  /** Tracks the previous effect's preload count to detect settings changes. */
  private lastEffectPreRenderCount: number | null = null;

  /**
   * Tracks the previous effect's `stationList` array reference. The provider
   * publishes a new array on every `setItems` (run add/remove and in-place
   * mutations like panorama rotation) but keeps the same reference across
   * tileset prefetch (which only writes to the non-reactive `tilesetCache`).
   * Comparing references is therefore a precise "did station data change?"
   * signal.
   */
  private lastEffectStationList: (ImageStation | null)[] | undefined;

  private textureReadySub?: Subscription;
  private textureLoadTimeoutId?: ReturnType<typeof setTimeout>;

  private readonly setStation: Setter<
    (ImageStation & { index: number }) | null
  >;
  private readonly stationList: Accessor<(ImageStation | null)[] | undefined>;
  protected logger = getLogger();

  constructor(
    stationsProvider: StationsCapabilityProvider,
    public readonly cameraName: string,
    viewer: Web3DViewer,
    web3dSettings: Accessor<Web3dSettings>,
    threeJsManager: ThreeJsManager,
    protected readonly opacityOptions?: ImageCapabilityOpacityOptions
  ) {
    super(viewer, stationsProvider, threeJsManager);

    const [isImageVisible, setIsImageVisible] = createSignal<boolean>(true);
    this.isImageVisible = isImageVisible;
    this.setIsImageVisible = setIsImageVisible;

    const [isCurrentStationImageReady, setIsCurrentStationImageReady] =
      createSignal<boolean>(true);
    this.isCurrentStationImageReady = isCurrentStationImageReady;
    this.setIsCurrentStationImageReady = setIsCurrentStationImageReady;

    const [isImageLoading, setIsImageLoading] = createSignal<boolean>(false);
    this.isImageLoading = isImageLoading;
    this.setIsImageLoading = setIsImageLoading;

    const [station, setStation] = createSignal<
      (ImageStation & { index: number }) | null
    >(null, {
      equals: (prev, next) => {
        if (prev === next) return true;
        if (prev == null || next == null) return false;
        return prev.station.id === next.station.id && prev.index === next.index;
      },
    });

    this.setStation = setStation;

    this.stationList = createMemo(() => {
      const stationList = this.stationsProvider
        .items()
        .find((s) => s.cameraName === this.cameraName)?.stationList;
      return stationList;
    });

    createEffect(
      on(
        [this.selectedStationIndex, this.stationList],
        ([selInd, stationList]) => {
          if (selInd == null || stationList == null) {
            this.setStation(null);
            return;
          }
          const st = stationList[selInd];
          this.setStation(st ? { ...st, index: selInd } : null);
        }
      )
    );

    createEffect(() => {
      const active = this.isActive();

      if (!active) {
        this.clearTextureWatch();
        this.setIsImageLoading(false);
        this.setIsCurrentStationImageReady(false);

        // Reset FOV to default when image capability deactivates.
        // Image capabilities are the only ones that change FOV (via zoom),
        // so they are responsible for cleaning up after themselves.
        viewer.camera.fieldOfView = DEFAULT_FIELD_OF_VIEW;

        // Snapshot before clearing — the async unload needs the refs.
        const stationsToUnload = new Map(this.loadedStations);
        this.loadedStations.clear();
        void this.unloadStationsSnapshot(stationsToUnload);
      }
    });

    // Slider changes don't re-trigger the loading effect — apply to the
    // current station only so preloads keep OPACITY_HIDDEN.
    createEffect(
      on(
        () => this.opacityOptions?.resolveVisibleStationOpacity?.(),
        () => {
          if (!this.isActive() || !this.isImageVisible()) return;

          const currentIndex = this.selectedStationIndex();
          if (currentIndex == null) return;

          const resolved = this.resolveStation(currentIndex);
          if (!resolved) return;

          const model = this.viewer.getModel(resolved.station.id) as
            | ImageModel
            | undefined;
          if (model) {
            model.setOpacity(this.resolveVisibleStationOpacity());
          }
        },
        { defer: true }
      )
    );

    createEffect(
      on(
        [
          station,
          this.isActive,
          () => web3dSettings(),
          this.isImageVisible,
          this.stationList,
          () => this.stationsProvider.getCameraCacheVersion(this.cameraName)(),
        ],
        ([
          currentStation,
          isActive,
          settings,
          isImageVisible,
          currentStationList,
          _cacheVer,
        ]) => {
          const currentIndex = currentStation?.index ?? null;
          const currentStationId = currentStation?.station?.id ?? null;
          const stationChanged =
            currentIndex !== this.lastEffectStationIndex ||
            currentStationId !== this.lastEffectStationId;
          const visibilityChanged =
            isImageVisible !== this.lastEffectImageVisible;
          const preRenderCount = settings.imageViewPreRenderStationCount;
          const settingsChanged =
            preRenderCount !== this.lastEffectPreRenderCount;
          const stationListChanged =
            currentStationList !== this.lastEffectStationList;
          this.lastEffectStationIndex = currentIndex;
          this.lastEffectStationId = currentStationId;
          this.lastEffectImageVisible = isImageVisible;
          this.lastEffectPreRenderCount = preRenderCount;
          this.lastEffectStationList = currentStationList;

          // If nothing important changed and the current station's model is
          // already loaded, skip the full re-evaluation. The ongoing async
          // load from the previous run naturally picks up new tileset-cache
          // entries via `resolveStation` (which reads from the non-reactive
          // `tilesetCache`). NOT incrementing `loadingEffectRunId` lets the
          // previous run continue.
          //
          // A new `stationList` array reference alone is not enough to fall
          // through: `setItems` (run add) `.slice()`s every camera's list,
          // including untouched ones. For those, loaded entries keep the
          // same id + object ref — bail to avoid spinner flicker. Fall
          // through only when a loaded entry's id or object ref changed
          // (regroup / remove / panorama rotation via
          // `updatePanoramaStationsForRun`). Empty `loadedStations` with a
          // new list ref still falls through so the load path can populate.
          const stationListMutated =
            stationListChanged &&
            (this.loadedStations.size === 0 ||
              this.hasLoadedStationMutations(currentStationList));
          if (
            !stationChanged &&
            !visibilityChanged &&
            !settingsChanged &&
            !stationListMutated &&
            isActive &&
            currentStation != null
          ) {
            const resolved = this.resolveStation(currentStation.index);
            if (resolved && this.viewer.getModel(resolved.station.id)) {
              return;
            }
          }

          this.clearTextureWatch();

          this.loadingEffectRunId += 1;
          const thisRunId = this.loadingEffectRunId;

          if (isActive && currentStation != null) {
            this.setIsImageLoading(true);
          }

          void (async () => {
            if (!isActive) return;

            this.viewer.camera.fieldOfView = DEFAULT_FIELD_OF_VIEW;

            if (currentStation == null) {
              this.setIsImageLoading(false);
              await this.unloadPreloadedStations();
              if (thisRunId === this.loadingEffectRunId) {
                this.loadedStations.clear();
              }
              return;
            }

            // Evict stale loaded models when either:
            //   1. The station ID at this index changed (re-group / run
            //      removal) — unload by recorded ID and let the load path
            //      pick up the new one.
            //   2. The live `stationList[index]` ref differs from the one we
            //      stored at load time — the provider replaced the entry
            //      in place (e.g. `updatePanoramaStationsForRun`).
            // We compare against the live ref, not `resolveStation(index)`:
            // resolved refs come from `tilesetCache` and drift on every
            // prefetch, which would mark prefetches as mutations.
            const stationListNow = currentStationList;
            // Same-id mutation evictions must be awaited before the load
            // phase below: `needsModel(i)` calls `viewer.getModel(id)` for
            // the same id we just kicked an unload for, so a fire-and-forget
            // unload would leave the model still present, drop the index
            // from `indicesToAttempt`, and the panorama would never reload.
            // Stale-id evictions (case 1) don't race: `needsModel` reads
            // the new id and correctly sees no model.
            const mutationUnloads: Promise<void>[] = [];
            for (const [index, loadedStation] of this.loadedStations) {
              const recordedId = loadedStation.station.id;
              const currentStationAtIndex = stationListNow?.[index] ?? null;
              const currentId = currentStationAtIndex?.station.id;
              if (currentId !== recordedId) {
                if (this.viewer.getModel(recordedId) != null) {
                  void this.viewer.unload(recordedId, { fitToView: false });
                }
                this.loadedStations.delete(index);
                continue;
              }
              if (
                currentStationAtIndex !== loadedStation &&
                this.viewer.getModel(recordedId) != null
              ) {
                mutationUnloads.push(
                  this.viewer.unload(recordedId, { fitToView: false })
                );
                this.loadedStations.delete(index);
              }
            }
            if (mutationUnloads.length > 0) {
              await Promise.all(mutationUnloads);
              if (thisRunId !== this.loadingEffectRunId) return;
            }

            const stationsToLoad = this.getStationsToLoad(
              currentStation.index,
              settings.imageViewPreRenderStationCount
            );

            const { newItems, removedItems, unChangedItems } =
              detectArrayChanges(
                Array.from(this.loadedStations.keys()),
                stationsToLoad
              );

            // Check whether the current station already has a loaded model.
            // If not, we defer opacity changes so the previous station stays
            // visible while the current one loads (prevents blank frames).
            const resolvedCurrent = this.resolveStation(currentStation.index);
            const currentModelReady = resolvedCurrent
              ? !!this.viewer.getModel(resolvedCurrent.station.id)
              : false;

            // Update opacity for stations that already have a model in the viewer.
            const stationsWithModels = [...unChangedItems, ...newItems];
            for (const index of stationsWithModels) {
              const imageStation = this.resolveStation(index);
              if (!imageStation) continue;
              const model = this.viewer.getModel(imageStation.station.id) as
                | ImageModel
                | undefined;
              if (model && currentModelReady) {
                model.setOpacity(
                  index === currentStation.index && isImageVisible
                    ? this.resolveVisibleStationOpacity()
                    : OPACITY_HIDDEN
                );
              }
            }

            // Position the camera immediately when the current station is
            // already loaded (fast path).
            if (currentModelReady) {
              this.setIsCurrentStationImageReady(true);
              this.setupCameraForStation(currentStation);
              this.startTextureLoadingWatch(
                resolvedCurrent!.station.id,
                thisRunId
              );
            }

            // ── Eagerly unload removed models BEFORE loading new ones ──
            // During fast navigation, runs are frequently superseded at the
            // first `await`.  Awaiting each unload one-by-one meant only ~1
            // model was removed per run while 1 new model was added — the
            // viewer accumulated models (and their active LOD analyzers)
            // unboundedly, exhausting browser connections.
            //
            // Fix: fire-and-forget all unloads (no await) so the entire loop
            // is synchronous.  `loadedStations` is fully trimmed before any
            // yield point, giving the next run an accurate baseline.  The
            // actual viewer.unload() completes asynchronously in the background.
            for (const index of removedItems) {
              const imageStation =
                this.stationList()?.[index] ?? this.loadedStations.get(index);
              if (imageStation) {
                void this.unloadImageStation(imageStation);
              }
              this.loadedStations.delete(index);
            }

            // Load new stations + retry unchanged ones that don't have a model yet
            const needsModel = (i: number): boolean => {
              const s = this.resolveStation(i);
              return !!s && !this.viewer.getModel(s.station.id);
            };
            const indicesToAttempt = [
              ...newItems.filter(needsModel),
              ...unChangedItems.filter(needsModel),
            ];

            // Ensure the current station is loaded first to minimize blank time
            const currentIdxPos = indicesToAttempt.indexOf(
              currentStation.index
            );
            if (currentIdxPos > 0) {
              const [current] = indicesToAttempt.splice(currentIdxPos, 1);
              indicesToAttempt.unshift(current);
            }

            let deferredSwapDone = currentModelReady;
            const actuallyLoadedNewIndices: number[] = [];
            for (const index of indicesToAttempt) {
              const imageStation = this.resolveStation(index);
              if (!imageStation) continue;

              const model = await this.loadImageStation(imageStation);

              if (thisRunId !== this.loadingEffectRunId) {
                if (model) {
                  void this.unloadImageStation(imageStation);
                }
                break;
              }

              if (model) {
                // Store the live `stationList[index]` ref (not `imageStation`,
                // which may be a `tilesetCache` copy with a different
                // identity) so the eviction loop's ref comparison stays
                // apples-to-apples. Fall back to `imageStation` if the live
                // list no longer has an entry, so unload by id still works.
                if (!this.loadedStations.has(index)) {
                  actuallyLoadedNewIndices.push(index);
                  const liveEntry = this.stationList()?.[index];
                  this.loadedStations.set(index, liveEntry ?? imageStation);
                }
                if (index === currentStation.index) {
                  this.setIsCurrentStationImageReady(true);
                  this.startTextureLoadingWatch(
                    imageStation.station.id,
                    thisRunId
                  );

                  // Hide old models BEFORE making this one visible and
                  // repositioning the camera.  On the deferred (slow) path the
                  // previous station's model is still visible; if we set the
                  // camera first, the renderer briefly shows BOTH models from
                  // the new viewpoint — producing a tilted/skewed image.
                  if (!deferredSwapDone) {
                    deferredSwapDone = true;
                    this.hideNonCurrentStations(currentStation.index, [
                      ...unChangedItems,
                      ...newItems,
                      ...actuallyLoadedNewIndices,
                    ]);
                  }
                  model.setOpacity(
                    isImageVisible
                      ? this.resolveVisibleStationOpacity()
                      : OPACITY_HIDDEN
                  );
                  this.setupCameraForStation(currentStation);
                } else {
                  model.setOpacity(OPACITY_HIDDEN);
                }
              } else if (index === currentStation.index && !deferredSwapDone) {
                this.setIsImageLoading(false);
                this.setIsCurrentStationImageReady(false);
                deferredSwapDone = true;
                this.hideNonCurrentStations(currentStation.index, [
                  ...unChangedItems,
                  ...newItems,
                  ...actuallyLoadedNewIndices,
                ]);
              }
            }

            if (thisRunId !== this.loadingEffectRunId) return;

            // Finalise camera + opacity when the current station was
            // confirmed loaded — either via the fast path (currentModelReady)
            // or via the deferred swap in the loading loop above.
            if (deferredSwapDone) {
              this.setupCameraForStation(currentStation);

              const currentModel = this.viewer.getModel(
                currentStation.station.id
              ) as ImageModel | undefined;
              if (currentModel && isImageVisible) {
                currentModel.setOpacity(this.resolveVisibleStationOpacity());
              }
            }
          })();
        }
      )
    );
  }

  /**
   * True when any loaded entry's id or object ref differs from the live
   * `stationList` — same comparison the stale-model eviction loop uses.
   */
  private hasLoadedStationMutations(
    stationList: (ImageStation | null)[] | undefined
  ): boolean {
    for (const [index, loadedStation] of this.loadedStations) {
      const currentStationAtIndex = stationList?.[index] ?? null;
      const currentId = currentStationAtIndex?.station.id;
      if (currentId !== loadedStation.station.id) {
        return true;
      }
      if (currentStationAtIndex !== loadedStation) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns the station at the given index, resolved from the tileset cache if
   * the provider's version has no URL. This keeps the provider stable (no array
   * churn) while still loading stations that prefetch has resolved.
   */
  private resolveStation(index: number): ImageStation | null {
    const imageStation = this.stationList()?.[index];
    if (!imageStation) return null;
    const hasUrl =
      imageStation.kind === 'planar'
        ? imageStation.station.tilesUrl !== ''
        : imageStation.station.tilesetInfo?.tilesetUrl !== undefined;
    if (hasUrl) return imageStation;
    return (
      this.stationsProvider.tilesetCache.get(imageStation.station.id) ??
      imageStation
    );
  }

  /**
   * Sets opacity to 0 for all loaded stations except the current one.
   * Used when deferring the opacity swap until the current station is ready.
   */
  private hideNonCurrentStations(
    currentStationIndex: number,
    indices: number[]
  ): void {
    for (const index of indices) {
      if (index === currentStationIndex) continue;
      const imageStation = this.resolveStation(index);
      if (!imageStation) continue;
      const model = this.viewer.getModel(imageStation.station.id) as
        | ImageModel
        | undefined;
      if (model) {
        model.setOpacity(OPACITY_HIDDEN);
      }
    }
  }

  /**
   * Override: Image capabilities show markers only when active.
   * This prevents multiple cameras from showing markers simultaneously.
   */
  protected override shouldShowStationMarkers(): boolean {
    return this.isActive();
  }

  protected resolveVisibleStationOpacity(): number {
    return (
      this.opacityOptions?.resolveVisibleStationOpacity?.() ?? OPACITY_VISIBLE
    );
  }

  protected abstract loadImageStation(
    imageStation: ImageStation
  ): Promise<ImageModel | undefined>;

  protected abstract setupCameraForStation(imageStation: ImageStation): void;

  private startTextureLoadingWatch(stationId: string, runId: number): void {
    this.clearTextureWatch();
    const model = this.viewer.getModel(stationId) as ImageModel | undefined;
    if (!model) {
      this.setIsImageLoading(false);
      return;
    }
    // subscribeToRootTextureLoaded uses a BehaviorSubject that emits synchronously
    // if the texture is already loaded — `sub` would not yet be assigned inside the callback.
    // The `done` flag lets us detect sync emission and unsubscribe after .subscribe() returns.
    let done = false;

    this.textureLoadTimeoutId = setTimeout(() => {
      if (!done && runId === this.loadingEffectRunId) {
        done = true;
        this.setIsImageLoading(false);
        this.clearTextureWatch();
      }
    }, TEXTURE_LOAD_TIMEOUT_MS);

    const sub = model.subscribeToRootTextureLoaded((loaded) => {
      if (loaded && !done) {
        done = true;
        clearTimeout(this.textureLoadTimeoutId);
        this.textureLoadTimeoutId = undefined;
        if (runId === this.loadingEffectRunId) {
          this.setIsImageLoading(false);
        }
      }
    });
    if (done) {
      sub.unsubscribe();
      clearTimeout(this.textureLoadTimeoutId);
      this.textureLoadTimeoutId = undefined;
      return;
    }
    this.textureReadySub = sub;
  }

  private clearTextureWatch(): void {
    this.textureReadySub?.unsubscribe();
    this.textureReadySub = undefined;
    clearTimeout(this.textureLoadTimeoutId);
    this.textureLoadTimeoutId = undefined;
  }

  /**
   * Use this in subclasses check if the image should be preloaded and set up FOV.
   * @returns true if image should be loaded.
   * @protected
   */
  protected imagePreload(): boolean {
    return this.isActive();
  }

  protected unloadImageStation = async (pano: ImageStation): Promise<void> => {
    const model = this.viewer.getModel(pano.station.id);
    if (model != null) {
      await this.viewer.unload(pano.station.id, { fitToView: false });
    }
  };

  public override getFrustumPosition(): Omit<
    PositioningData,
    'cameraPosition'
  > {
    const currentIndex = this.selectedStationIndex();
    const stations = this.stationsProvider.stationsInfo();

    if (
      currentIndex != null &&
      currentIndex >= 0 &&
      currentIndex < stations.length
    ) {
      return {
        targetPosition: stations[currentIndex].position,
        quaternion: this.viewer.camera.quaternion.toArray(),
        fov: this.viewer.camera.fieldOfView,
      };
    }

    return super.getFrustumPosition();
  }

  public hasLoadedImage(): boolean {
    const currentIndex = this.selectedStationIndex();
    const stations = this.stationList();
    if (currentIndex == null || !stations || currentIndex < 0) {
      return false;
    }

    const station = stations[currentIndex];
    if (!station) {
      return false;
    }

    return this.viewer.getModel(station.station.id) != null;
  }

  public override async cleanup(): Promise<void> {
    this.logger.debug(
      `ImageCapabilityBase ${this.cameraName}: cleanup, deactivate`
    );
    this.clearTextureWatch();
    await this.unloadPreloadedStations();
    this.loadedStations.clear();
    await super.cleanup();
  }

  private getStationsToLoad(
    currentStationIndex: number | null,
    preRenderStationCount: number
  ): number[] {
    if (currentStationIndex == null) return [];
    const stationList = this.stationList() ?? [];
    return [
      currentStationIndex,
      ...Array.from({ length: preRenderStationCount }, (_, i) => i + 1)
        .flatMap((offset) => [
          currentStationIndex - offset,
          currentStationIndex + offset,
        ])
        .filter((index) => index >= 0 && index < stationList.length),
    ];
  }

  private async unloadPreloadedStations(): Promise<void> {
    for (const [index, loadedStation] of this.loadedStations) {
      // Prefer the current station list (it's the live source), fall back
      // to the recorded reference for the case where the upstream list has
      // been cleared (e.g. run unloaded) — we still know what we loaded.
      const station = this.stationList()?.[index] ?? loadedStation;
      await this.unloadImageStation(station);
    }
  }

  private async unloadStationsSnapshot(
    snapshot: Map<number, ImageStation>
  ): Promise<void> {
    for (const [, station] of snapshot) {
      await this.unloadImageStation(station);
    }
  }
}
